import React, { useRef } from 'react';
import styled from 'styled-components';
import { colors, typography } from '../../../styles/theme';
import { industries } from '../../../data/industries';
import { industries as beautyIndustries } from '../../../data/industries_beauty';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DialogContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const DialogTitle = styled.h3`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.lg};
  color: ${colors.text.primary};
  margin: 0;
  font-weight: ${typography.fontWeight.medium};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: ${colors.text.secondary};
  font-size: 20px;
  line-height: 1;
  
  &:hover {
    color: ${colors.text.primary};
  }
`;

const DialogContent = styled.div`
  margin-bottom: 24px;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px;
  border: 2px solid ${colors.border};
  border-radius: 4px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 4px ${colors.primary}15;
  }

  &::placeholder {
    color: ${colors.text.secondary};
  }

  &:disabled {
    background: ${colors.background};
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const CancelButton = styled(Button)`
  background: none;
  border: 1px solid ${colors.border};
  color: ${colors.text.secondary};
  
  &:hover {
    background: ${colors.background};
  }
`;

const SaveButton = styled(Button)`
  background: ${colors.primary};
  border: none;
  color: white;
  
  &:hover {
    background: ${colors.primary}ee;
  }
`;

const AutocompleteDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 2px solid ${colors.border};
  border-radius: 12px;
  max-height: 240px;
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const AutocompleteItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${colors.text.primary};
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};

  &:hover {
    background: ${colors.background};
  }

  &:first-child {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  &:last-child {
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

const EditDialog = ({ 
  isOpen, 
  onClose, 
  onSave, 
  title, 
  fieldLabel, 
  initialValue,
  fieldType = 'text',
  field
}) => {
  const [value, setValue] = React.useState(initialValue);
  const [filteredOptions, setFilteredOptions] = React.useState([]);
  const dropdownRef = useRef(null);

  // Reset value when dialog opens with new initialValue
  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Handle click outside dropdown
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFilteredOptions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);

    // Handle industry autocomplete
    if (field === 'industry') {
      const filtered = industries.filter(industry => 
        industry.toLowerCase().includes(newValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    // Handle sub-industry autocomplete
    else if (field === 'subIndustry') {
      const filtered = beautyIndustries.filter(industry => 
        industry.toLowerCase().includes(newValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  };

  const handleOptionSelect = (selectedValue) => {
    setValue(selectedValue);
    setFilteredOptions([]);
  };

  const handleSave = () => {
    onSave(value);
    onClose();
  };

  const renderInput = () => {
    console.log('Rendering input for field:', field);
    console.log('Current value:', value);
    console.log('Filtered options:', filteredOptions);

    switch (field) {
      case 'industry':
      case 'subIndustry':
        return (
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <Input
              type="text"
              value={value}
              onChange={handleInputChange}
              placeholder={fieldLabel}
              autoFocus
            />
            {filteredOptions.length > 0 && (
              <AutocompleteDropdown>
                {filteredOptions.map((option, index) => (
                  <AutocompleteItem
                    key={index}
                    onClick={() => handleOptionSelect(option)}
                  >
                    {option}
                  </AutocompleteItem>
                ))}
              </AutocompleteDropdown>
            )}
          </div>
        );
      default:
        return (
          <Input
            type={fieldType}
            value={value}
            onChange={handleInputChange}
            placeholder={fieldLabel}
            autoFocus
          />
        );
    }
  };

  return (
    <Overlay onClick={onClose}>
      <DialogContainer onClick={e => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </DialogHeader>
        <DialogContent>
          {renderInput()}
        </DialogContent>
        <ButtonGroup>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <SaveButton onClick={handleSave}>Save Changes</SaveButton>
        </ButtonGroup>
      </DialogContainer>
    </Overlay>
  );
};

export default EditDialog; 